import React, { useMemo } from 'react';
import { RouteProps } from 'react-router';
import { Route, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';

// Selectors
import { selectFeatureFlags } from 'app/shared/featureFlags/selectors';

// Utils
import { ROUTES_MAP } from 'app/shared/utils/routes';
import {
  anyPermissionChecker,
  hasPermissionChecker,
} from 'app/modules/permissions/utils';
import { selectSessionAgentPermissions } from 'app/modules/session/selectors';
import { Permission } from 'app/shared/utils/permissions';
import { FeatureFlag } from 'app/shared/featureFlags/models';

interface OwnProps extends Pick<RouteProps, 'component' | 'exact' | 'path'> {
  permissions?: Permission | Permission[]; // like "read:alerts"
  anyPermissions?: Permission[];
  requiredFlags?: FeatureFlag | FeatureFlag[];
}

const mapStateToProps = (state: RootState) => ({
  allPermissions: selectSessionAgentPermissions(state),
  featureFlags: selectFeatureFlags(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

// defined separately for memoization purposes
const DEFAULT_PERMISSIONS = [];

// handles auth and read permissions
const ProtectedRoute = ({
  permissions = DEFAULT_PERMISSIONS,
  anyPermissions = DEFAULT_PERMISSIONS,
  allPermissions,
  exact,
  path,
  component,
  requiredFlags,
  featureFlags,
}: Props) => {
  const hasAnyPermissions = (): boolean => {
    if (allPermissions.length === 0) {
      return false;
    }
    return true;
  };

  const hasRequiredFeatureFlags = (): boolean => {
    if (requiredFlags) {
      const flagsToCheck = Array.isArray(requiredFlags)
        ? requiredFlags
        : [requiredFlags];
      return flagsToCheck.every((flag) => Boolean(featureFlags[flag]));
    }
    return true;
  };

  const hasPermissions = useMemo(
    () =>
      anyPermissionChecker(allPermissions, anyPermissions) &&
      hasPermissionChecker(
        allPermissions,
        typeof permissions === 'string' ? [permissions] : permissions,
      ),
    [allPermissions, anyPermissions, permissions],
  );

  if (hasPermissions && hasRequiredFeatureFlags()) {
    return <Route exact={exact} path={path} component={component} />;
  }

  // redirecting to the home page if the user does not have access to a certain feature
  if (hasAnyPermissions()) {
    return <Redirect to="/" />;
  }
  // Redirect to error page if user doesn't have any permissions
  return <Redirect to={ROUTES_MAP.error.path} />;
};

export default connect(mapStateToProps)(ProtectedRoute);
